import { arraysEqual } from "helpers/system";
import { useLocalStorage } from "hooks/useLocalStorage";
import React, { forwardRef, useEffect, useMemo } from "react";
import { LayersControl, MapContainer, Pane, TileLayer, useMap } from "react-leaflet";
import { useMapEvents } from "react-leaflet/hooks";

const layerRefs = {};
const tileTypePriority = {
    DoubleGisPaid: 0,
    2: 0,
    DoubleGis: 1,
    0: 1,
    Osm: 2,
    1: 2,
};

const getTilePriority = (tile) => tileTypePriority[tile.type] ?? Number.MAX_SAFE_INTEGER;

const sortTileUrlsByPriority = (tileUrls) =>
    tileUrls
        .map((tileUrl, index) => ({ index, tileUrl }))
        .sort(
            (left, right) =>
                getTilePriority(left.tileUrl) - getTilePriority(right.tileUrl) || left.index - right.index,
        )
        .map(({ tileUrl }) => tileUrl);

const TileLayerWithFallback = ({ item, layer, setLayer, tileUrls, mapRef }) => {
    const eventHandlers = useMemo(
        () => ({
            loading() {
                setLayer(item.title);
            },
            tileerror(error) {
                if (!navigator.onLine) {
                    return;
                }
                console.error(error);
                const index = tileUrls.findIndex((tile) => tile.title === layer);
                if (tileUrls[index + 1] && mapRef) {
                    layerRefs[item.title].remove();
                    layerRefs[tileUrls[index + 1].title].addTo(mapRef);
                    setLayer(tileUrls[index + 1].title);
                }
            },
        }),
        [item.title, layer, mapRef, setLayer, tileUrls],
    );

    return (
        <TileLayer
            ref={(l) => (layerRefs[item.title] = l)}
            eventHandlers={eventHandlers}
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url={item.url}
            {...item.options}
        />
    );
};

const MapTemplate = forwardRef(({ children, tileUrls, mapRef, viewport, onViewportChanged, ...props }, ref) => {
    const sortedTileUrls = useMemo(() => sortTileUrlsByPriority(tileUrls), [tileUrls]);
    const preferredLayer = sortedTileUrls[0]?.title;
    const [layer, setLayer] = useLocalStorage("selectedTile", preferredLayer);
    const hasSelectedLayer = sortedTileUrls.some((tileUrl) => tileUrl.title === layer);
    const activeLayer = hasSelectedLayer ? layer : preferredLayer;

    useEffect(() => {
        if (preferredLayer && !hasSelectedLayer && layer !== preferredLayer) {
            setLayer(preferredLayer);
        }
    }, [hasSelectedLayer, layer, preferredLayer, setLayer]);

    if (sortedTileUrls.length === 1) {
        return (
            <MapContainer {...props} {...viewport} ref={ref}>
                <TileLayer
                    url={sortedTileUrls[0].url}
                    {...sortedTileUrls[0].options}
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <Pane name="region-drying-periods" />
                {children}
                {!props.withoutViewportCheck && (
                    <>
                        <ViewportChangeHandler onViewportChanged={onViewportChanged} />
                        <ChangeViewport {...viewport} />
                    </>
                )}
            </MapContainer>
        );
    }

    return (
        <MapContainer {...viewport} {...props} ref={ref} attributionControl={false}>
            <LayersControl position="topleft">
                {sortedTileUrls.map((item) => (
                    <LayersControl.BaseLayer name={item.title} key={item.title} checked={item.title === activeLayer}>
                        <TileLayerWithFallback
                            item={item}
                            layer={activeLayer}
                            setLayer={setLayer}
                            tileUrls={sortedTileUrls}
                            mapRef={mapRef}
                        />
                    </LayersControl.BaseLayer>
                ))}
            </LayersControl>
            <Pane name="region-drying-periods" />
            {children}
            {!props.withoutViewportCheck && (
                <>
                    <ViewportChangeHandler onViewportChanged={onViewportChanged} />
                    <ChangeViewport {...viewport} />
                </>
            )}
        </MapContainer>
    );
});

export default MapTemplate;

const ViewportChangeHandler = (props) => {
    const map = useMapEvents({
        zoomend: () => {
            const center = map.getCenter();
            props.onViewportChanged?.({ zoom: map.getZoom(), center: [center.lat, center.lng] });
        },
        moveend: () => {
            const center = map.getCenter();
            props.onViewportChanged?.({ zoom: map.getZoom(), center: [center.lat, center.lng] });
        },
    });
    return null;
};

const ChangeViewport = ({ zoom, center }) => {
    const map = useMap();
    if (!map._loaded) {
        return null;
    }
    const { lat, lng } = map.getCenter();

    if (!zoom) {
        zoom = map.getZoom();
    }
    if (!center) {
        center = [lat, lng];
    }

    if (map.getZoom() !== zoom || !arraysEqual([lat, lng], center)) {
        setTimeout(() => {
            map.setView(center, zoom);
        }, 0);
    }
    return null;
};
